import { useState } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { canResync, canRunTests, pickBanner, primaryAction, setupProgress, usage } from './derive'
import { Capabilities, type Capability } from './parts/Capabilities'
import { Hero } from './parts/Hero'
import { RecentTests } from './parts/RecentTests'
import { SetupGuide } from './parts/SetupGuide'
import { OnboardingCall } from './parts/OnboardingCall'
import { OnboardingDetails } from './parts/OnboardingDetails'
import { FooterHelp, HomeBanner, HomeSkeleton, PrivateByDesign } from './parts/Small'
import type { HomeData, LoadState } from './types'
import type { Busy, HomeActions } from './useHome'
import { trackShopifyAction } from '../../shopifyUiTracking'

export interface HomePageViewProps {
  state: LoadState
  busy: Busy
  actions: HomeActions
  shopify: ShopifyAppBridge
}

/**
 * TestFeed app home, presentational. Every state comes from `state`/`busy`/`actions`; see derive.ts for
 * the rules. HomePage.tsx is the thin container that calls useHome()/useAppBridge() and renders this —
 * Ladle stories render HomePageView directly with a fixture LoadState, matching this repo's
 * props-over-internal-fetching convention (see e.g. ShopifyHomePage.stories.tsx).
 */
export function HomePageView({ state, busy, actions, shopify }: HomePageViewProps) {
  const copy = shopifyComponentCopy.appHome
  const [focusGuide, setFocusGuide] = useState(0)

  if (state.kind === 'loading') return <HomeSkeleton />

  // No data and nothing saved. The page still explains TestFeed so the visit isn't wasted.
  if (state.kind === 'error') {
    return (
      <s-page>
        <s-query-container>
          <s-stack gap="base">
            <s-banner tone="critical" heading={copy.loadErrorHeading}>
              {state.message}
              {copy.loadErrorSuffix}
              <s-button
                slot="secondary-actions"
                variant="secondary"
                loading={busy === 'retry'}
                onClick={actions.retryLoad}
              >
                {copy.connectTryAgainAction}
              </s-button>
            </s-banner>
            <Hero cta={null} />
            <Capabilities
              items={capabilities(null, actions.go, actions.newTest)}
              onLocked={() => shopify.toast?.show(copy.loadErrorLoadedToastMessage)}
            />
            <FooterHelp />
          </s-stack>
        </s-query-container>
      </s-page>
    )
  }

  const { data, stale } = state
  const progress = setupProgress(data)
  const ready = canRunTests(data)
  const primary = primaryAction(data)
  const banner = pickBanner(data, stale)
  const guideVisible = !data.prefs.guideDismissed
  const heroVisible = !data.prefs.heroDismissed || !progress.complete
  const openGuide = () =>
    trackShopifyAction(shopify, 'open-guide', () => {
      if (data.prefs.guideDismissed) actions.savePrefs({ guideDismissed: false })
      setFocusGuide((n) => n + 1)
    })
  const upgrade = actions.choosePlan

  const heroCta = ready
    ? { label: copy.startTestAction, onClick: () => actions.newTest() }
    : progress.complete
      ? null
      : { label: progress.done === 0 ? copy.startSetupAction : copy.continueSetupAction, onClick: openGuide }

  return (
    <s-page>
      {primary && (
        <s-button
          slot="primary-action"
          variant="primary"
          onClick={primary.do === 'new-test' ? () => actions.newTest() : upgrade}
        >
          {primary.label}
        </s-button>
      )}
      {!guideVisible && !progress.complete && (
        <s-button slot="secondary-actions" onClick={openGuide}>
          {copy.showSetupGuideAction}
        </s-button>
      )}
      {data.sync.status === 'done' && (
        <s-button
          slot="secondary-actions"
          icon="refresh"
          disabled={!canResync(data)}
          loading={busy === 'resync'}
          onClick={actions.resync}
        >
          {data.sync.refresh?.status === 'running' ? copy.resyncingAction : copy.resyncAction}
        </s-button>
      )}
      {data.prefs.heroDismissed && progress.complete && (
        <s-button slot="secondary-actions" onClick={() => actions.savePrefs({ heroDismissed: false })}>
          {copy.howItWorksAction}
        </s-button>
      )}

      <s-query-container>
        <s-stack gap="base">
          {banner && (
            <HomeBanner
              spec={banner}
              busy={
                busy === 'retry' ||
                (busy === 'sync' && banner.id === 'sync-failed') ||
                (busy === 'resync' && banner.id === 'resync-failed')
              }
              onDismiss={() => actions.dismissBanner(banner.id)}
              onAction={(a) =>
                a === 'retry-load'
                  ? actions.retryLoad()
                  : a === 'retry-sync'
                    ? actions.startSync()
                    : a === 'resync'
                      ? actions.resync()
                      : upgrade()
              }
            />
          )}

          {heroVisible && (
            <Hero
              cta={heroCta}
              onDismiss={progress.complete ? () => actions.savePrefs({ heroDismissed: true }) : undefined}
            />
          )}

          {guideVisible && <SetupGuide data={data} busy={busy} actions={actions} focusSignal={focusGuide} />}

          {!guideVisible && <OnboardingCall shopify={shopify} />}
          <OnboardingDetails shopify={shopify} />

          <Capabilities
            items={capabilities(data, actions.go, actions.newTest)}
            onLocked={(c) => {
              shopify.toast?.show(c.lockedReason ?? copy.capabilitiesLockedFallbackReason)
              if (!progress.complete) openGuide()
            }}
          />

          <RecentTests
            tests={data.tests}
            usage={usage(data)}
            go={actions.go}
            mode={
              ready
                ? { kind: 'ready', onNewTest: () => actions.newTest() }
                : data.sync.status === 'done'
                  ? { kind: 'paused' }
                  : { kind: 'setup' }
            }
          />

          {!guideVisible && <PrivateByDesign go={actions.go} />}

          <FooterHelp />
        </s-stack>
      </s-query-container>
    </s-page>
  )
}

function capabilities(
  data: HomeData | null,
  go: (to: string) => void,
  newTest: (k?: 'product' | 'store') => void,
): Capability[] {
  const copy = shopifyComponentCopy.appHome
  const testsLocked = !data
    ? copy.capabilitiesNotLoadedReason
    : data.sync.status === 'done' && data.plan.status === 'cancelled'
      ? copy.capabilitiesTestsLockedCancelledReason
      : canRunTests(data)
        ? null
        : copy.capabilitiesTestsLockedReason
  const insightsLocked = !data
    ? copy.capabilitiesNotLoadedReason
    : data.sync.status === 'done'
      ? null
      : copy.capabilitiesInsightsLockedReason
  return [
    {
      kind: 'products',
      group: copy.capProductsGroup as Capability['group'],
      title: copy.capProductsTitle,
      body: copy.capProductsBody,
      open: () => newTest('product'),
      lockedReason: testsLocked,
    },
    {
      kind: 'store',
      group: copy.capStoreGroup as Capability['group'],
      title: copy.capStoreTitle,
      body: copy.capStoreBody,
      open: () => newTest('store'),
      lockedReason: testsLocked,
    },
    {
      kind: 'product-insights',
      group: copy.capProductInsightsGroup as Capability['group'],
      title: copy.capProductInsightsTitle,
      body: copy.capProductInsightsBody,
      open: () => go('/insights'),
      lockedReason: insightsLocked,
    },
    {
      kind: 'insights',
      group: copy.capInsightsGroup as Capability['group'],
      title: copy.capInsightsTitle,
      body: copy.capInsightsBody,
      open: () => go('/insights'),
      lockedReason: insightsLocked,
    },
    {
      kind: 'opportunities',
      group: copy.capOpportunitiesGroup as Capability['group'],
      title: copy.capOpportunitiesTitle,
      body: copy.capOpportunitiesBody,
      open: () => go('/app/opportunities'),
      lockedReason: insightsLocked,
    },
  ]
}
